/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { readOnly, setDiff, gt } from '@ember/object/computed';
import computedFirst from 'cio/utils/computed-first';
import SegmentModel from 'cio/models/segment';
import AuthService from 'cio/services/auth';
import { task } from 'ember-concurrency';
import { timeout, Task } from 'ember-concurrency';
import { action } from '@ember/object';
import AnalyticsService from 'cio/services/analytics';
import ExportsService from 'cio/services/exports';
import { TimeRange } from 'cio/utils/time';
import { getDefaultSegmentMembershipRange } from 'cio/utils/metrics/segment-membership';
import moment from 'moment-timezone';
import CustomerService from 'cio/services/customer';
import { queryEncode } from 'cio/helpers/query';
import { Grouping } from 'cio/utils/metrics/resolution';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import { tracked } from '@glimmer/tracking';

interface SegmentHeaderEditArgs {
  segment: SegmentModel;
  isDisabled?: boolean;
  isImportingCSV?: boolean;
  peopleCount?: number;
  isLoadingPeopleCount?: boolean;
  clearTask: Task<unknown, []>;
  deleteTask: Task<unknown, []>;
  archiveTask: Task<unknown, []>;
  segmentMembershipChangeExportRange: any;
}

const CUTOFF = 5;

export default class SegmentHeaderEdit extends Component<SegmentHeaderEditArgs> {
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service customer!: CustomerService;
  @service exports!: ExportsService;
  @service facebookLeadAds!: FacebookLeadAdsService;

  @tracked showAllNewsletters = false;
  @tracked segmentMembershipChangeExportModalState: 'hide' | 'export' | 'exporting' = 'hide';
  @tracked segmentMembershipChangeExportTimeRange = getDefaultSegmentMembershipRange();

  @tracked isClearModalOpen = false;
  @tracked isDeleteModalOpen = false;
  @tracked peopleListExportModalState: 'hide' | 'exporting' = 'hide';
  @tracked confirmArchiveModalState: 'hide' | 'show' = 'hide';

  exportPendingImage = new URL('cio/assets/images/export-pending.svg', import.meta.url).toString();
  exportSuccessImage = new URL('cio/assets/images/export-success.svg', import.meta.url).toString();
  exportFailureImage = new URL('cio/assets/images/export-failure.svg', import.meta.url).toString();

  @readOnly('args.segment.usage.draftNewsletters') newsletters!: Record<string, any>[];
  @computedFirst('newsletters', CUTOFF) newslettersBeforeCutoff!: Record<string, any>[];
  @setDiff('newsletters', 'newslettersBeforeCutoff') newslettersAfterCutoff!: Record<string, any>[];
  @gt('newsletters.length', 0) isUsedInNewsletters!: boolean;

  get peopleCount() {
    return this.args.peopleCount ?? 0;
  }

  get hasReadOnlyPermission() {
    return (
      !this.auth.hasPermission('journeys/segments/create') &&
      !this.auth.hasPermission('journeys/segments/edit') &&
      !this.auth.hasPermission('journeys/segments/delete')
    );
  }

  @computed(
    'args.isDisabled',
    'args.{clearTask,deleteTask,archiveTask,loadPeopleCount}.isRunning',
    'args.isLoadingPeopleCount',
  )
  get disabled() {
    return (
      Boolean(this.args.isDisabled) ||
      this.hasReadOnlyPermission ||
      this.args.clearTask?.isRunning ||
      this.args.deleteTask?.isRunning ||
      this.args.archiveTask?.isRunning ||
      Boolean(this.args.isLoadingPeopleCount)
    );
  }

  get segmentHasPeople() {
    return this.peopleCount !== 0;
  }

  trackDuplicateManualSegment = task({ restartable: true }, async () => {
    await timeout(250);
    this.trackDuplication(this.args.segment);
  });

  @computed(
    'args.segment.{type,archived,usedInAnyCampaigns,usedInActiveCampaigns,usedInActiveCampaignsOrUnsentNewsletters}',
    'args.segment.usage.dashboard',
    'args.disabled',
  )
  get dropdownActions() {
    let { segment } = this.args;

    let archiveToggleDisabled = this.disabled || (!segment.archived && !segment.canBeArchived);

    let archiveToggleTooltip = segment.isLinkedToConnectedForm
      ? `This segment is connected to a form, so you can't archive it.`
      : `You can't archive a segment that's in use.`;

    if (
      (segment.archived && !this.auth.hasPermission('journeys/segments/create')) ||
      (!segment.archived && !this.auth.hasPermission('journeys/segments/delete'))
    ) {
      archiveToggleTooltip = 'Contact an Admin to access this feature.';
      archiveToggleDisabled = true;
    }

    let deleteTooltip = segment.isLinkedToAnyNonDeletedForm
      ? `This segment is connected to a form, so you can't delete it.`
      : "You can't delete a segment that's in use.";
    if (!this.auth.hasPermission('journeys/segments/delete')) {
      deleteTooltip = 'Contact an Admin to access this feature.';
    }

    let actions = [
      {
        label: 'Duplicate Segment',
        disabled: this.disabled || !segment.isDynamic || !this.auth.hasPermission('journeys/segments/create'),
        action: this.trackDuplication.bind(this, segment),
        link: {
          route: 'segments.new',
          query: { referenceSegmentId: segment.id },
        },
        hasTooltip: true,
        tooltip: {
          condition: !segment.isDynamic || !this.auth.hasPermission('journeys/segments/create'),
          content: this.auth.hasPermission('journeys/segments/create')
            ? 'Only available for Data-driven Segments'
            : 'Contact an Admin to access this feature.',
          direction: 'left',
        },
        onMouseEnter: segment.isDynamic
          ? null
          : this.trackDuplicateManualSegment.perform.bind(this.trackDuplicateManualSegment, segment),
        onMouseLeave: this.cancelTrackDuplicateManualSegment,
      },
      {
        label: segment.archived ? 'Unarchive Segment' : 'Archive Segment',
        disabled: archiveToggleDisabled,
        action:
          (segment.usage?.dashboard ?? []).length > 0 && !segment.archived
            ? () => (this.confirmArchiveModalState = 'show')
            : this.args.archiveTask.perform.bind(this.args.archiveTask),
        tooltip: {
          condition: archiveToggleDisabled,
          content: archiveToggleTooltip,
          direction: 'left',
        },
      },
      {
        label: 'Delete Segment',
        disabled: this.disabled || !segment.canBeDeleted || !this.auth.hasPermission('journeys/segments/delete'),
        action: this.toggleDeleteModal,
        tooltip: {
          condition:
            segment.isLinkedToAnyNonDeletedForm ||
            segment.usedInAnyCampaigns ||
            segment.usedInUnsentNewsletters ||
            !this.auth.hasPermission('journeys/segments/delete'),
          content: deleteTooltip,
        },
      },
    ];

    if (!segment.isDynamic) {
      actions.splice(2, 0, {
        label: 'Clear Segment',
        disabled: this.disabled || !this.auth.hasPermission('journeys/segments/edit'),
        action: this.toggleClearModal,
        tooltip: {
          condition: !this.auth.hasPermission('journeys/segments/edit'),
          content: 'Contact an Admin to access this feature.',
        },
      } as any);
    }

    return actions;
  }

  @action
  trackDuplication(segment: SegmentModel) {
    this.analytics.track('segment', 'duplicated', { model: segment });
  }

  @action
  cancelTrackDuplicateManualSegment() {
    this.trackDuplicateManualSegment.cancelAll();
  }

  @action
  onSegmentMembershipChangeExportRangeSelect(range: TimeRange) {
    this.segmentMembershipChangeExportTimeRange = range;
  }

  @action
  showSegmentMembershipChangeExportModal() {
    this.segmentMembershipChangeExportModalState = 'export';
    this.segmentMembershipChangeExportTimeRange = this.args.segmentMembershipChangeExportRange;
  }

  @action
  startSegmentMembershipChangeExport() {
    this.segmentMembershipChangeExportModalState = 'exporting';
  }

  @action
  createSegmentMembershipChangeExport() {
    let { start, end } = this.segmentMembershipChangeExportTimeRange as any;
    let adjustedStart = moment.tz(`${start.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC');
    let adjustedEnd = moment.tz(`${end.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC');

    let data = {
      id: this.args.segment.id,
      start: adjustedStart.unix(),
      end: adjustedEnd.unix(),
      resolution: Grouping.Daily,
    };

    return this.exports.segmentMembershipMetrics(data);
  }

  @action
  createPeopleListExport() {
    let { segment } = this.args;

    let filters = queryEncode([
      {
        type: 'segments_negatable',
        segments: [segment.id],
        negate: false,
      },
    ]);

    return this.customer.exports({ filters, attributes: null, from: 'segment_people' });
  }

  @action
  confirmArchiveFromModal() {
    this.confirmArchiveModalState = 'hide';
    this.args.archiveTask.perform();
  }

  @action
  toggleClearModal() {
    this.isClearModalOpen = !this.isClearModalOpen;
  }

  @action
  toggleDeleteModal() {
    this.isDeleteModalOpen = !this.isDeleteModalOpen;
  }
}
